SidePanelActionsService.$inject = [
	'mx.shell.ContextService',
	'mx.shell.EventService',
	'mx.shell.SidePanelService',
	'mx.shell.ActionManagerService',
	'mx.shell.NotificationService',
	'mx.shell.InternationalizationService',
	'mx.internationalization',
	'$window',
	'mx.SolutionBuilderAgent.ContentWidgetService',
];

/**
 * @ngdoc service
 * @name mx.shell.SidePanelActionsService
 * @module mx.shell
 *
 * @requires {@ref mx.shell.DetailsView mx.shell.DetailsView}
 * @requires {@ref mx.shell.ContextService mx.shell.ContextService}
 * @requires {@ref mx.shell.SidePanelService mx.shell.SidePanelService}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 * @requires {@ref mx.shell.ActionManagerService mx.shell.ActionManagerService}
 * @requires {@ref mx.shell.NotificationService mx.shell.NotificationService}
 * @requires {@ref mx.internationalization mx.internationalization}
 * @requires $window
 * @requires $rootScope
 *
 * @property {array} commonSelectedItemsActions - common actions for selected items
 * @property {array} uiActions - a list of `uiActions`.
 *        This `uiActions` - it's special buttons on top of {@ref mx.shell.SidePanelService SidePanelService},
 *        that allows to execute something when user click on them.<br>
 *        Examples of `uiActions`: "Open in Excel", "Copy to clipboard", "Open in new window"...<br>
 *        `uiActions` can be unique for each {@ref mx.shell.DetailsView DetailsView}.
 *        {@ref mx.shell.DetailsView DetailsView} has an property `uiActionGroup` as well as each `uiAction` has it
 *        by which {@ref mx.shell.SidePanelService SidePanelService} knows what `uiActions` should be shown
 *        when some {@ref mx.shell.DetailsView DetailsView} is shown.
 *        There are two system `uiActionGroup`s:
 *            - "selection" (for selection tab);
 *            - "details_view" (default).
 * @description
 * Controls SidePanel actions.
 */
export function SidePanelActionsService(
	contextService,
	eventService,
	sidePanelService,
	actionManagerService,
	notificationService,
	i18nService,
	i18n,
	$window,
	contentWidgetService
) {
	var self = {
		actionTypes: [mx.shell.ActionContextType.None, mx.shell.ActionContextType.Single, mx.shell.ActionContextType.Multiple],
		addUIAction: addUIAction,
		calculateCommonActions: calculateCommonActionsForSelectedObjects,
		calculateViewActions: calculateViewActions,
		getUIActions: getUIActions,
		uiActions: [],
	};

	addUIAction(
		'with-corresponding-url',
		i18n.get('shell.common.open-in-new-window'),
		function () {
			if (this.correspondingUrl) {
				var search = window.location.search ? window.location.search.replace(/view-options=.+?(?:&|$)/gi, '') : '?';

				var last = search[search.length - 1];
				if (last !== '&' && last !== '?') {
					search = search + '&';
				}

				$window.open(window.location.pathname + search + this.correspondingUrl, '_blank');
			} else {
				notificationService.error(i18n.get('shell.common.no-URL-for-view'));
			}
		},
		'open-in-new-window-menu-item'
	);

	addUIAction(
		'is_printable',
		i18n.get('shell.common.print'),
		function () {
			if (this.printUrl) {
				$window.open(this.printUrl, '_blank');
			} else {
				notificationService.error(i18n.get('shell.common.no-URL-for-view'));
			}
		},
		'print-menu-item'
	);

	addUIAction(
		'is_configurable',
		i18n.get('solutionBuilderAgent.common.configure'),
		function () {
			contentWidgetService.openConfigurePreview(sidePanelService.currentView);
		},
		'configure-menu-item',
		true
	);

	addUIAction(
		'is_customizable',
		i18n.get('shell.common.changeLayout'),
		function () {
			contentWidgetService.customizeViewInDesigner(sidePanelService.currentView);
		},
		'customize-menu-item',
		true
	);

	i18nService.getLanguages().then(function (langs) {
		if (langs && langs.length > 1) {
			_registerLocalizationAction();
		}
	});

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelActionsService#addUIAction
	 *
	 * @description
	 * defines new `uiAction'
	 *
	 * @param {string} uiActionGroup - a group by which actions will be grouped and
	 *        connected with {@ref mx.shell.DetailsView DetailsView} which has a property with the same name
	 * @param {string} label - a label, that will be shown on UI
	 * @param {function} handler - a handler that will be executed when User click on this `uiAction` on UI.
	 * @param {string} name - action technical name (also used as automation testing tag).
	 * @param {boolean} ariaHidden - hide action for screen-readers
	 *
	 * @returns {object} this new `uiAction'
	 */
	function addUIAction(uiActionGroup, label, handler, name, ariaHidden) {
		var actionObj = {
			handler: handler,
			label: label,
			name: name,
			uiActionGroup: uiActionGroup,
			ariaHidden: ariaHidden,
		};
		self.uiActions.push(actionObj);
		return actionObj;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelActionsService#getUIActions
	 *
	 * @description
	 * returns a list of `uiActions` by passed `uiActionGroup`
	 *
	 * @param {string} uiActionGroup - a group by which actions are grouped and
	 *        connected with {@ref mx.shell.DetailsView DetailsView} which has a property with the same name
	 *
	 * @returns {array} a list of `uiActions` by passed `uiActionGroup`
	 */
	function getUIActions(uiActionGroup) {
		return self.uiActions.filter(function (uiAction) {
			return (
				typeof uiActionGroup !== 'undefined' &&
				((Array.isArray(uiActionGroup) && uiActionGroup.indexOf(uiAction.uiActionGroup) !== -1) || uiAction.uiActionGroup === uiActionGroup)
			);
		});
	}

	function calculateCommonActionsForSelectedObjects(selectedObjects) {
		// If debounced call / no `selectedObjects` argument passed
		if (!Array.isArray(selectedObjects)) {
			selectedObjects = contextService.selectedObjects;
		}

		var objectTypes = selectedObjects
			// Get array of types
			.map(function (item) {
				return item._type;
			})
			// Filter just unique types
			.filter(function (_type, i, newArr) {
				return _type && newArr.indexOf(_type) === i;
			});

		var actionContext = new mx.shell.ActionsContext(
			objectTypes,
			contextService.actionsContext ? contextService.actionsContext.extraActions : [],
			mx.shell.ActionUiZone.SidePanel,
			contextService.actionsContext && contextService.actionsContext.hideActions
		);

		return actionManagerService.getActions(actionContext, self.actionTypes, selectedObjects).then(function (actions) {
			return actionManagerService.filterActions(actions, null, selectedObjects);
		});
	}

	function calculateViewActions(view) {
		if (view.isWizard) {
			return;
		}

		var viewContext = view.context || { _type: view.type };
		var objectTypes = [viewContext._type];
		var _selectedObjects = (view.locals && view.locals.selectedObjects) || [viewContext];

		var hideActions = contextService.actionsContext && contextService.actionsContext.hideActions;

		var actionContext = new mx.shell.ActionsContext(
			objectTypes,
			contextService.actionsContext ? contextService.actionsContext.extraActions : [],
			view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit ? mx.shell.ActionUiZone.EditDialog : mx.shell.ActionUiZone.SidePanel,
			hideActions
		);

		actionManagerService
			.getActions(actionContext, self.actionTypes, _selectedObjects)
			.then(function (actions) {
				return actionManagerService.filterActions(actions, view, _selectedObjects);
			})
			.then(function (filteredActions) {
				view.__actions = filteredActions;
				eventService.dispatchEvent(mx.shell.EventCodes.ActionsReady, { view: view, actions: filteredActions });
			});
	}

	function _registerLocalizationAction() {
		var localizationSwitchAction = addUIAction(
			'localizable_view',
			i18n.get('shell.common.enable-localization-mode'),
			function () {
				var currentView = sidePanelService.getCurrentView();
				currentView.__localizationModeEnabled = !currentView.__localizationModeEnabled;
				eventService.dispatchEvent(mx.shell.EventCodes.SwitchLocalizationMode, { view: currentView });
				_setLocalizationActionLabelForCurrentView(currentView);
			},
			'localization-mode-menu-item'
		);
		var _setLocalizationActionLabelForCurrentView = function (currentView) {
			localizationSwitchAction.label = i18n.get('shell.common.' + (currentView && currentView.__localizationModeEnabled ? 'disable' : 'enable') + '-localization-mode');
		};
		eventService.subscribeGlobal(mx.shell.EventCodes.SidePanelCurrentViewChanged, function (_, view) {
			_setLocalizationActionLabelForCurrentView(view);
		});
	}

	return self;
}
